import { useState, useRef } from "react";
import RevealSection from "@/components/base/RevealSection";
import { submitNetlifyForm } from "@/utils/netlifyForms";

const DECISION_TREE_URL = "/decision-tree.pdf";
const CONVERSATION_GUIDE_URL = "/what-conversations-do-i-need.pdf";

const STEPS = [
  {
    id: 1,
    question: "Is my time, energy, or workload being overstepped?",
    yesTarget: "Boundary",
    noNext: 2,
  },
  {
    id: 2,
    question: "Is someone's behaviour or output affecting results?",
    yesTarget: "Feedback",
    noNext: 3,
  },
  {
    id: 3,
    question: "Is there tension, disagreement, or friction?",
    yesTarget: "Conflict",
    noNext: 4,
  },
  {
    id: 4,
    question: "Have I already tried to resolve this directly?",
    yesTarget: "Escalation",
    noNext: 5,
  },
  {
    id: 5,
    question: "Did a conversation go badly or damage trust?",
    yesTarget: "Repair",
    noTarget: "Pause & Reassess",
  },
];

const TARGETS: Record<string, { icon: string; color: string; bg: string; desc: string; action: string }> = {
  Boundary: {
    icon: "ri-shield-line",
    color: "text-navy",
    bg: "bg-teal",
    desc: "You need a Boundary conversation. Set a clear, calm limit without apology.",
    action: "Set the Boundary",
  },
  Feedback: {
    icon: "ri-chat-check-line",
    color: "text-navy",
    bg: "bg-teal",
    desc: "You need a Feedback conversation. Be specific, kind, and direct.",
    action: "Give the Feedback",
  },
  Conflict: {
    icon: "ri-team-line",
    color: "text-navy",
    bg: "bg-teal",
    desc: "You need a Conflict conversation. Name the tension and invite a solution together.",
    action: "Address the Conflict",
  },
  Escalation: {
    icon: "ri-alert-line",
    color: "text-navy",
    bg: "bg-amber-600",
    desc: "You need an Escalation conversation. Take it up — briefly, calmly, with facts.",
    action: "Escalate Calmly",
  },
  Repair: {
    icon: "ri-heart-3-line",
    color: "text-navy",
    bg: "bg-teal",
    desc: "You need a Repair conversation. Own your part, name the impact, rebuild trust.",
    action: "Start the Repair",
  },
  "Pause & Reassess": {
    icon: "ri-pause-circle-line",
    color: "text-navy",
    bg: "bg-seafoam",
    desc: "Nothing needs a conversation right now. Sleep on it. If it still matters tomorrow, come back.",
    action: "Pause & Reassess",
  },
};

function DecisionTreeInteractive() {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [history, setHistory] = useState<string[]>([]);
  const [result, setResult] = useState<string | null>(null);

  const handleAnswer = (answer: "yes" | "no") => {
    const step = STEPS[currentStep];
    if (answer === "yes" && step.yesTarget) {
      setHistory((h) => [...h, `Q${step.id}: Yes → ${step.yesTarget}`]);
      setResult(step.yesTarget);
    } else if (answer === "no") {
      if (step.noNext) {
        setHistory((h) => [...h, `Q${step.id}: No`]);
        setCurrentStep(step.noNext - 1);
      } else if (step.noTarget) {
        setHistory((h) => [...h, `Q${step.id}: No → ${step.noTarget}`]);
        setResult(step.noTarget);
      }
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setHistory([]);
    setResult(null);
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Starting point */}
      <div className="flex flex-col items-center mb-8 md:mb-10">
        <div className="bg-navy text-white text-sm md:text-base font-semibold px-6 py-3 rounded-lg text-center max-w-md">
          Something feels uncomfortable or unresolved at work
        </div>
        <div className="w-px h-6 bg-navy/30 my-1" />
      </div>

      {result ? (
        <div className="flex flex-col items-center">
          {/* History trail */}
          <div className="w-full space-y-2 mb-6">
            {history.map((entry, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-white border border-seafoam/50 rounded-lg px-4 py-2.5 text-sm text-navy/70"
              >
                <span className="w-6 h-6 rounded-full bg-seafoam/30 flex items-center justify-center text-xs font-bold text-navy flex-shrink-0">
                  {i + 1}
                </span>
                {entry}
              </div>
            ))}
          </div>

          {/* Result card */}
          <div className="w-full bg-white border-2 border-seafoam rounded-2xl p-6 md:p-10 text-center">
            <div
              className={`w-16 h-16 md:w-20 md:h-20 rounded-full ${TARGETS[result].bg} flex items-center justify-center mx-auto mb-5`}
            >
              <i className={`${TARGETS[result].icon} text-white text-2xl md:text-3xl`} />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-navy mb-3">
              {result}
            </h3>
            <p className="text-navy/70 text-base md:text-lg leading-relaxed max-w-lg mx-auto mb-6">
              {TARGETS[result].desc}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://katiahunt.gumroad.com/l/scripts"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-teal text-white font-semibold px-6 py-3 rounded-lg hover:bg-navy transition-all text-sm whitespace-nowrap"
              >
                Get the Script Pack
              </a>
              <button
                onClick={reset}
                className="inline-flex items-center justify-center border-2 border-teal text-teal font-semibold px-6 py-3 rounded-lg hover:bg-teal hover:text-white transition-all text-sm whitespace-nowrap"
              >
                <i className="ri-restart-line mr-2" />
                Start Over
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          {/* Question history */}
          {history.length > 0 && (
            <div className="w-full space-y-2 mb-6">
              {history.map((entry, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 bg-white border border-seafoam/50 rounded-lg px-4 py-2.5 text-sm text-navy/70"
                >
                  <span className="w-6 h-6 rounded-full bg-seafoam/30 flex items-center justify-center text-xs font-bold text-navy flex-shrink-0">
                    {i + 1}
                  </span>
                  {entry}
                </div>
              ))}
            </div>
          )}

          {/* Current question */}
          <div className="w-full bg-white border-2 border-teal/30 rounded-2xl p-6 md:p-10 text-center">
            <span className="inline-block text-teal text-xs font-bold uppercase tracking-wider mb-4">
              Question {STEPS[currentStep].id} of 5
            </span>
            <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-navy mb-8 leading-relaxed">
              {STEPS[currentStep].question}
            </h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => handleAnswer("yes")}
                className="w-full sm:w-auto inline-flex items-center justify-center bg-teal text-white font-semibold px-8 py-3.5 rounded-lg hover:bg-navy transition-all text-base whitespace-nowrap"
              >
                <i className="ri-check-line mr-2" />
                Yes
              </button>
              <button
                onClick={() => handleAnswer("no")}
                className="w-full sm:w-auto inline-flex items-center justify-center border-2 border-teal text-teal font-semibold px-8 py-3.5 rounded-lg hover:bg-teal hover:text-white transition-all text-base whitespace-nowrap"
              >
                <i className="ri-close-line mr-2" />
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VisualTreeOverview() {
  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="flex flex-col items-center">
        {/* Root */}
        <div className="bg-navy text-white text-sm md:text-base font-semibold px-6 py-3 rounded-lg text-center">
          Something feels uncomfortable or unresolved at work
        </div>

        {/* Connector */}
        <div className="w-px h-5 bg-navy/20" />

        {/* Questions */}
        <div className="w-full space-y-4 md:space-y-5">
          {STEPS.map((step, i) => (
            <div key={step.id} className="flex flex-col items-center">
              {/* Question node */}
              <div className="w-full max-w-lg bg-white border border-seafoam rounded-xl p-4 md:p-5 text-center">
                <span className="text-teal text-xs font-bold uppercase tracking-wider block mb-1">
                  Question {step.id}
                </span>
                <p className="text-navy font-medium text-sm md:text-base">
                  {step.question}
                </p>
              </div>

              {/* Yes/No branches */}
              <div className="flex items-start justify-center gap-6 md:gap-12 mt-3 w-full">
                <div className="flex flex-col items-center">
                  <div className="w-px h-4 bg-navy/15" />
                  <div className="bg-teal text-white text-xs md:text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap">
                    Yes → {step.yesTarget}
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-px h-4 bg-navy/15" />
                  {step.noNext ? (
                    <div className="bg-seafoam text-navy text-xs md:text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap">
                      No → Continue
                    </div>
                  ) : (
                    <div className="bg-seafoam text-navy text-xs md:text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap">
                      No → {step.noTarget}
                    </div>
                  )}
                </div>
              </div>

              {/* Continue connector */}
              {step.noNext && (
                <div className="w-px h-5 bg-navy/15 mt-3" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DecisionTree() {
  const [mode, setMode] = useState<"interactive" | "overview">("interactive");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    const form = e.currentTarget;
    try {
      await submitNetlifyForm(new FormData(form));
      setFormSubmitted(true);
      form.reset();
    } catch (err) {
      console.error(err);
      setFormError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Title Area */}
      <RevealSection>
        <section className="bg-white px-4 md:px-8 lg:px-12 pt-16 md:pt-24 pb-8 md:pb-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl lg:text-[48px] font-bold text-navy ca-page-title mb-5">
              Workplace Conversation Decision Tree
            </h1>
            <p className="text-navy/70 text-base md:text-lg lg:text-xl leading-relaxed max-w-3xl mx-auto">
              Not sure what kind of conversation you need? Answer five simple
              questions to identify whether you need a boundary, feedback,
              conflict, escalation, or repair conversation.
            </p>
          </div>
        </section>
      </RevealSection>

      {/* Mode Toggle */}
      <RevealSection delay={100}>
        <section className="bg-white px-4 md:px-8 lg:px-12 pb-6 md:pb-8">
          <div className="max-w-3xl mx-auto flex items-center justify-center">
            <div className="inline-flex bg-seafoam/20 rounded-full p-1">
              <button
                onClick={() => setMode("interactive")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  mode === "interactive"
                    ? "bg-teal text-white"
                    : "text-navy hover:text-teal"
                }`}
              >
                Interactive
              </button>
              <button
                onClick={() => setMode("overview")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  mode === "overview"
                    ? "bg-teal text-white"
                    : "text-navy hover:text-teal"
                }`}
              >
                Full Overview
              </button>
            </div>
          </div>
        </section>
      </RevealSection>

      {/* Interactive or Overview */}
      <RevealSection delay={150}>
        <section className="bg-seafoam/15 px-4 md:px-8 lg:px-12 py-10 md:py-16">
          {mode === "interactive" ? (
            <DecisionTreeInteractive />
          ) : (
            <VisualTreeOverview />
          )}
        </section>
      </RevealSection>

      {/* Quote */}
      <RevealSection delay={100}>
        <section className="bg-white px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <blockquote className="text-xl md:text-2xl lg:text-[28px] font-medium text-navy leading-relaxed italic">
              &ldquo;Clarity beats avoidance. One calm sentence is often enough.&rdquo;
            </blockquote>
          </div>
        </section>
      </RevealSection>

      {/* Footer Question */}
      <RevealSection delay={100}>
        <section className="bg-navy px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-seafoam text-lg md:text-xl lg:text-2xl font-light leading-relaxed">
              What is the minimum effective conversation that stops this getting worse?
            </p>
          </div>
        </section>
      </RevealSection>

      {/* Download Section */}
      <RevealSection delay={100}>
        <section className="bg-white px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="max-w-2xl mx-auto flex flex-col items-center gap-4">
            <button
              onClick={scrollToForm}
              className="w-full max-w-md inline-flex items-center justify-center bg-teal text-white font-semibold px-6 py-4 rounded-lg hover:bg-navy transition-all duration-300 text-base text-center whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line mr-2 text-lg flex-shrink-0" />
              Download the Decision Tree
            </button>
          </div>
        </section>
      </RevealSection>

      {/* Lead Capture Form */}
      <RevealSection delay={100}>
        <section ref={formRef} className="bg-seafoam/10 px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-10 border border-seafoam/30">
              {formSubmitted ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-5">
                    <i className="ri-check-line text-teal text-2xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-navy mb-3">You&apos;re all set!</h3>
                  <p className="text-navy/70 text-base leading-relaxed mb-6 max-w-md mx-auto">
                    Thank you — your Decision Tree and conversation guide are ready to download.
                  </p>
                  <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-4">
                    <a
                      href={DECISION_TREE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full max-w-sm items-center justify-center bg-teal text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-navy transition-colors text-base text-center"
                    >
                      <i className="ri-download-line mr-2 flex-shrink-0" />
                      <span>Download the Decision Tree</span>
                    </a>
                    <a
                      href={CONVERSATION_GUIDE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full max-w-sm items-center justify-center border-2 border-teal text-teal font-semibold px-5 py-3.5 rounded-lg hover:bg-teal hover:text-white transition-colors text-base text-center leading-snug"
                    >
                      <i className="ri-download-line mr-2 flex-shrink-0" />
                      <span>Download “What Conversation Do I Actually Need?”</span>
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-xl md:text-2xl font-bold text-navy mb-2">
                    Get the Decision Tree
                  </h2>
                  <p className="text-navy/60 text-sm md:text-base mb-6 leading-relaxed">
                    Enter your details below to access the Decision Tree and the “What Conversation Do I Actually Need?” guide.
                  </p>
                  <form
                    name="decision_tree_download"
                    method="POST"
                    data-netlify="true"
                    onSubmit={handleFormSubmit}
                    className="flex flex-col gap-4"
                  >
                    <input type="hidden" name="form-name" value="decision_tree_download" />
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1 flex flex-col gap-1.5">
                        <label htmlFor="dt_first_name" className="text-sm font-medium text-navy">
                          First Name <span className="text-teal">*</span>
                        </label>
                        <input
                          id="dt_first_name"
                          type="text"
                          name="first_name"
                          required
                          placeholder="Jane"
                          className="w-full border border-seafoam/50 rounded-lg px-4 py-3 text-sm text-navy placeholder-navy/30 focus:outline-none focus:border-teal transition-colors"
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-1.5">
                        <label htmlFor="dt_last_name" className="text-sm font-medium text-navy">
                          Last Name <span className="text-teal">*</span>
                        </label>
                        <input
                          id="dt_last_name"
                          type="text"
                          name="last_name"
                          required
                          placeholder="Smith"
                          className="w-full border border-seafoam/50 rounded-lg px-4 py-3 text-sm text-navy placeholder-navy/30 focus:outline-none focus:border-teal transition-colors"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="dt_email" className="text-sm font-medium text-navy">
                        Email Address <span className="text-teal">*</span>
                      </label>
                      <input
                        id="dt_email"
                        type="email"
                        name="email"
                        required
                        placeholder="jane@example.com"
                        className="w-full border border-seafoam/50 rounded-lg px-4 py-3 text-sm text-navy placeholder-navy/30 focus:outline-none focus:border-teal transition-colors"
                      />
                    </div>
                    {formError && (
                      <p className="text-red-500 text-sm">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-teal text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-navy transition-colors text-base whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {submitting ? "Sending..." : "Send Me the Decision Tree"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* Bottom CTA */}
      <RevealSection delay={100}>
        <section className="bg-seafoam/20 px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-navy/60 text-base md:text-lg mb-4">
              Need the words, training or workplace support to go with the decision?
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-[36px] font-bold text-navy mb-8">
              Choose Your Next Step
            </h2>
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
              <a
                href="https://katiahunt.gumroad.com/l/scripts"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-teal text-white font-semibold px-8 py-4 rounded-lg hover:bg-navy transition-all duration-300 text-base whitespace-nowrap"
              >
                Get the Script Pack
              </a>
              <a
                href="https://katiahunt.gumroad.com/l/course"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center border-2 border-teal text-teal font-semibold px-8 py-4 rounded-lg hover:bg-teal hover:text-white transition-all duration-300 text-base whitespace-nowrap"
              >
                Get the Course
              </a>
              <a
                href="/corporate-enquiries"
                className="inline-flex items-center justify-center border-2 border-navy text-navy font-semibold px-8 py-4 rounded-lg hover:bg-navy hover:text-white transition-all duration-300 text-base whitespace-nowrap"
              >
                Corporate Enquiries
              </a>
            </div>
          </div>
        </section>
      </RevealSection>
    </div>
  );
}